import { b2x } from '@b2x/react/src';
import classNames from 'classnames';
import React from 'react';

import { Container } from '../Container';
import { PageBuilderContentType } from '../contentTypes';
import { AssetA } from '../pageBuilder/AssetA';
import { CopyA } from '../pageBuilder/CopyA';
import { CopyB } from '../pageBuilder/CopyB';
import { CopyC } from '../pageBuilder/CopyC';
import { CopyPageTitleA } from '../pageBuilder/CopyPageTitleA';
import { DoubleColumnA } from '../pageBuilder/DoubleColumnA';
import { DoubleColumnB } from '../pageBuilder/DoubleColumnB';
import { DynamicColumnsA } from '../pageBuilder/DynamicColumnsA';
import { GalleryA } from '../pageBuilder/GalleryA';
import { InstagramA } from '../pageBuilder/InstagramA';
import { ProductsA } from '../pageBuilder/ProductsA';
import { ProductsB } from '../pageBuilder/ProductsB';
import { SliderA } from '../pageBuilder/SliderA';
import { Page } from './Page';
import { usePopupNewsletterModal } from './PopupNewsletterModal';

export interface PageBuilderProps {
  demo?: boolean;
}

export const PageBuilder = ({ demo }: PageBuilderProps) => {
  const page = b2x.usePage<PageBuilderContentType>();
  const body = page?.content?.body;

  const { pageReady } = b2x.useAppContext();

  const [PopupNewsLetterModal, showPopupNewsLetterModal] = usePopupNewsletterModal({
    onClose: () => {
      const modalClosedTimes = (b2x.storage.getNumber('homePageNewsletterModalClosedTimes', true) ?? 0) + 1;
      b2x.storage.setNumber('homePageNewsletterModalClosedTimes', modalClosedTimes, true);
    },
  });

  const showPopup = page?.content?.body.showPopupSection?.showPopup;
  React.useEffect(() => {
    if (!showPopup) {
      return;
    }

    const newsletterConsent = b2x.storage.getBoolean('newsletterConsent', true);
    if (newsletterConsent) {
      return;
    }

    const modalClosedTimes = b2x.storage.getNumber('homePageNewsletterModalClosedTimes', true) ?? 0;
    if (modalClosedTimes >= 2 || !pageReady) {
      return;
    }

    const dispose = setTimeout(showPopupNewsLetterModal, 3000 * (modalClosedTimes + 1));
    // Pulisco il timeout quando il componente viene smontato, vuol dire che è stata cambiata pagina
    // se no la callback viene eseguita ma la referenza a showPopupNewsLetterModal non esiste più
    return () => clearTimeout(dispose);
  }, [pageReady, showPopupNewsLetterModal, showPopup]);

  return (
    <Page
      assetOverlayColor={body?.pageBackground?.assetOverlayColor}
      background={body?.pageBackground?.color}
      backgroundAsset={body?.pageBackground?.asset?.type?.at(0) ? body.pageBackground.asset : undefined}
      className="page-builder"
      noPaddingBottom={body?.pageOptions?.noPaddingBottom}
      noPaddingTop={body?.pageOptions?.noPaddingTop}
      thingsToLoadBeforePageReady={[page]}
      transparentHeader
    >
      {showPopup && PopupNewsLetterModal}

      {page?.content?.body.sections?.map((section) => {
        // Section background
        const background = b2x.getItemFromSwitch(section.options?.background);
        // Section margin
        const _marginTop = section.options?.margin?.top;
        const _marginBottom = section.options?.margin?.bottom;
        const marginTop = {
          lg: demo ? 0 : _marginTop?.lg === 'default' ? undefined : _marginTop?.lg,
          md: demo ? 0 : _marginTop?.md === 'default' ? undefined : _marginTop?.md,
          sm: demo ? 0 : _marginTop?.sm === 'default' ? undefined : _marginTop?.sm,
          xl: demo ? 0 : _marginTop?.xl === 'default' ? undefined : _marginTop?.xl,
          xs: demo ? 0 : _marginTop?.xs === 'default' ? undefined : _marginTop?.xs,
          xxl: demo ? 0 : _marginTop?.xxl === 'default' ? undefined : _marginTop?.xxl,
        } as b2x.ResponsiveMarginSize;
        const marginBottom = {
          lg: demo ? 0 : _marginBottom?.lg === 'default' ? 3 : _marginBottom?.lg,
          md: demo ? 0 : _marginBottom?.md === 'default' ? 3 : _marginBottom?.md,
          sm: demo ? 0 : _marginBottom?.sm === 'default' ? 3 : _marginBottom?.sm,
          xl: demo ? 0 : _marginBottom?.xl === 'default' ? 5 : _marginBottom?.xl,
          xs: demo ? 0 : _marginBottom?.xs === 'default' ? 3 : _marginBottom?.xs,
          xxl: demo ? 0 : _marginBottom?.xxl === 'default' ? 5 : _marginBottom?.xxl,
        } as b2x.ResponsiveMarginSize;
        // Section padding
        const _paddingTop = section.options?.padding?.top;
        const _paddingBottom = section.options?.padding?.bottom;
        // Se è settato default, ed è presente un background viene impostato un padding top di default per dare "aria" al componente
        const paddingTop = {
          lg: _paddingTop?.lg === 'default' ? (background ? 5 : undefined) : _paddingTop?.lg,
          md: _paddingTop?.md === 'default' ? (background ? 3 : undefined) : _paddingTop?.md,
          sm: _paddingTop?.sm === 'default' ? (background ? 3 : undefined) : _paddingTop?.sm,
          xl: _paddingTop?.xl === 'default' ? (background ? 5 : undefined) : _paddingTop?.xl,
          xs: _paddingTop?.xs === 'default' ? (background ? 3 : undefined) : _paddingTop?.xs,
          xxl: _paddingTop?.xxl === 'default' ? (background ? 5 : undefined) : _paddingTop?.xxl,
        } as b2x.ResponsivePaddingSize;
        // Se è settato default, ed è presente un background viene impostato un padding bottom di default per dare "aria" al componente
        const paddingBottom = {
          lg: _paddingBottom?.lg === 'default' ? (background ? 5 : undefined) : _paddingBottom?.lg,
          md: _paddingBottom?.md === 'default' ? (background ? 3 : undefined) : _paddingBottom?.md,
          sm: _paddingBottom?.sm === 'default' ? (background ? 3 : undefined) : _paddingBottom?.sm,
          xl: _paddingBottom?.xl === 'default' ? (background ? 5 : undefined) : _paddingBottom?.xl,
          xs: _paddingBottom?.xs === 'default' ? (background ? 3 : undefined) : _paddingBottom?.xs,
          xxl: _paddingBottom?.xxl === 'default' ? (background ? 5 : undefined) : _paddingBottom?.xxl,
        } as b2x.ResponsivePaddingSize;
        // Section component
        const sectionComponent = b2x.getItemFromSwitch(section.component);
        let componentName;
        let component = null;
        if (sectionComponent?.assetA) {
          componentName = 'assetA';
          component = <AssetA {...sectionComponent.assetA} />;
        }
        if (sectionComponent?.sliderA) {
          componentName = 'sliderA';
          component = <SliderA {...sectionComponent.sliderA} />;
        }
        if (sectionComponent?.dynamicColumnsA) {
          componentName = 'dynamicColumnsA';
          component = <DynamicColumnsA {...sectionComponent.dynamicColumnsA} />;
        }

        if (sectionComponent?.copyPageTitleA) {
          componentName = 'copyPageTitleA';
          component = <CopyPageTitleA {...sectionComponent.copyPageTitleA} />;
        }

        if (sectionComponent?.doubleColumnA) {
          componentName = 'doubleColumnA';
          component = <DoubleColumnA {...sectionComponent.doubleColumnA} />;
        }

        if (sectionComponent?.doubleColumnB) {
          componentName = 'doubleColumnB';
          component = <DoubleColumnB {...sectionComponent.doubleColumnB} />;
        }

        if (sectionComponent?.copyA) {
          componentName = 'copyA';
          component = <CopyA {...sectionComponent.copyA} />;
        }

        if (sectionComponent?.copyB) {
          componentName = 'copyB';
          component = <CopyB {...sectionComponent.copyB} />;
        }

        if (sectionComponent?.copyC) {
          componentName = 'copyC';
          component = <CopyC {...sectionComponent.copyC} />;
        }

        if (sectionComponent?.galleryA) {
          componentName = 'galleryA';
          component = <GalleryA {...sectionComponent.galleryA} />;
        }

        if (sectionComponent?.productsA) {
          componentName = 'productsA';
          component = <ProductsA {...sectionComponent.productsA} />;
        }

        if (sectionComponent?.productsB) {
          componentName = 'productsB';
          component = <ProductsB {...sectionComponent.productsB} />;
        }

        if (sectionComponent?.instagramA) {
          componentName = 'instagramA';
          component = <InstagramA {...sectionComponent.instagramA} />;
        }
        return (
          <React.Fragment key={section.contentSectionId}>
            {section.options?.enabled && (
              <React.Fragment>
                {demo && componentName && <ComponentName componentName={componentName} />}
                <b2x.Section
                  background={background?.color || background?.variant}
                  className={classNames('page-builder-section', section.options.class)}
                  marginBottom={marginBottom}
                  marginTop={marginTop}
                  paddingBottom={paddingBottom}
                  paddingTop={paddingTop}
                  style={{ overflow: 'hidden', position: 'relative' }}
                >
                  {background?.asset && (
                    <b2x.Div
                      style={{ height: '100%', left: 0, position: 'absolute', top: 0, width: '100%', zIndex: 10 }}
                    >
                      <b2x.AssetAsBackgroundV2 {...background.asset} />
                    </b2x.Div>
                  )}
                  <b2x.ConditionalWrapper
                    condition={background?.asset !== undefined}
                    wrapper={<b2x.Div style={{ position: 'relative', zIndex: 20 }} />}
                  >
                    {component}
                  </b2x.ConditionalWrapper>
                </b2x.Section>
              </React.Fragment>
            )}
          </React.Fragment>
        );
      })}
    </Page>
  );
};

interface ComponentNameProps {
  componentName: string;
}

const ComponentName = ({ componentName }: ComponentNameProps) => (
  <b2x.Div background="#EFEFEF" paddingY={4} textAlign={'center'}>
    <Container>
      <b2x.H3 margin={0}>
        <strong>{componentName}</strong>
        {/* <br /> */}
        {/* <Icon name={b2x.appConfig.icons?.arrow?.bottom} size={20} /> */}
      </b2x.H3>
    </Container>
  </b2x.Div>
);
